import React, { useEffect, useReducer, useState } from "react";
import {
  Row,
  Col,
  Select,
  Button,
  Table,
  Popconfirm,
  Tooltip,
  Input,
  Modal,
  Form,
  DatePicker,
  InputNumber,
  List,
  notification,
} from "antd";
import "../Pages/CSS/DonnesBases.css";
import { BpiService } from "../Services/BpiService";
import * as XLSX from "xlsx";
import {
  DeleteFilled,
  EditOutlined,
  PlusOutlined,
  SyncOutlined,
  ShoppingCartOutlined,
} from "@ant-design/icons";
import BonPdf from "./BonPdf";
import TextArea from "antd/es/input/TextArea";
import { FournissDiversService } from "../Services/FournissDiversService";
import { ArticlesAchatService } from "../Services/ArticlesAchatService";
import { InfoGenService } from "../Services/InfoGenService";
import moment from "moment";
import dayjs from "dayjs";
const { Option } = Select;

const initialState = {
  tableData: [],
  fournis: [],
  articlesData: [],
  infosGenData: [],
  loading: false,
  filteredData: [],
  selectedYear: new Date().getFullYear(),
  selectedMonth: new Date().getMonth() + 1,
  selectedArticle: null,
  addedRows: [],
  isContactInputDisabled: true,
  selectedArticlePrice: null,
  refreshTable: false,
  isModalVisible: false,
  totalHT: 0,
  totalRemise: 0,
  totalTVA: 0,
  totalTTC: 0,
  isEditing: false,
  editingItem: null,
  editingRowKey: null,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "SET_STATE":
      return { ...state, ...action.payload };
    case "RESET_FIELDS":
      return {
        ...state,
        addedRows: [],
        totalHT: 0,
        totalRemise: 0,
        totalTVA: 0,
        totalTTC: 0,
        filteredData: state.tableData,
      };
    default:
      return state;
  }
};

const BonsAchat = () => {
  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    tableData,
    fournis,
    articlesData,
    infosGenData,
    loading,
    filteredData,
    selectedYear,
    selectedMonth,
    selectedArticle,
    addedRows,
    isContactInputDisabled,
    selectedArticlePrice,
    refreshTable,
    isModalVisible,
    totalHT,
    totalRemise,
    totalTVA,
    totalTTC,
    isEditing,
    editingItem,
    editingRowKey,
  } = state;

  const [pageSize, setPageSize] = useState(10);

  const infosGenService = new InfoGenService();
  const articlesService = new ArticlesAchatService();
  const fournisseurService = new FournissDiversService();
  const bpiService = new BpiService();

  useEffect(() => {
    const fetchData = async () => {
      dispatch({ type: "SET_STATE", payload: { loading: true } });
      try {
        const [bons, fournisData, articles, infos] = await Promise.all([
          bpiService.getBonAchat(),
          fournisseurService.getAllFournissDivers(),
          articlesService.getAllArticlesAchat(),
          infosGenService.getInfosGen(),
        ]);
        dispatch({
          type: "SET_STATE",
          payload: {
            tableData: bons,
            fournis: fournisData,
            articlesData: articles,
            infosGenData: infos,
            loading: false,
            filteredData: bons,
          },
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des données :", error);
        dispatch({ type: "SET_STATE", payload: { loading: false } });
      }
    };
    fetchData();
  }, [refreshTable]);

  useEffect(() => {
    dispatch({ type: "SET_STATE", payload: { filteredData: tableData } });
  }, [tableData]);

  const handleDeleteLine = async (numba) => {
    try {
      await bpiService.deleteBon(numba);
      dispatch({
        type: "SET_STATE",
        payload: {
          filteredData: filteredData.filter((item) => item.numba !== numba),
        },
      });
    } catch (error) {
      console.error("Erreur lors de la suppression du document :", error);
    }
  };

  const handleSearch = (value) => {
    const filtered = tableData.filter((item) =>
      Object.values(item).some(
        (val) =>
          val && val.toString().toLowerCase().includes(value.toLowerCase())
      )
    );
    dispatch({ type: "SET_STATE", payload: { filteredData: filtered } });
  };

  const handleRefresh = () => {
    dispatch({ type: "SET_STATE", payload: { refreshTable: !refreshTable } });
  };

  const handleFournisseurSelect = (value) => {
    const selectedFournisseur = fournis.find(
      (fourni) => fourni.compte === value
    );
    if (
      selectedFournisseur &&
      selectedFournisseur.contact &&
      selectedFournisseur.tel_contact
    ) {
      form.setFieldsValue({
        contact: `${selectedFournisseur.contact} - ${selectedFournisseur.tel_contact}`,
      });
      dispatch({
        type: "SET_STATE",
        payload: { isContactInputDisabled: false },
      });
    } else {
      form.setFieldsValue({ contact: "" });
      dispatch({
        type: "SET_STATE",
        payload: { isContactInputDisabled: true },
      });
    }
  };

  const handleAddRow = () => {
    const values = form.getFieldsValue();
    if (
      values.qte !== undefined &&
      values.remise !== undefined &&
      values.tva !== undefined
    ) {
      const m_ht = values.qte * selectedArticlePrice;
      const m_htRemise = m_ht - (m_ht * values.remise) / 100;
      const m_ttc = m_htRemise + (m_htRemise * values.tva) / 100;
      const m_tva = (m_ht * values.tva) / 100;

      const newRow = {
        code_art: selectedArticle ? selectedArticle.code_art : "",
        designation_article: selectedArticle
          ? selectedArticle.designation_article
          : "",
        qte: values.qte,
        prix_unitaire: selectedArticlePrice,
        m_remise: values.remise,
        m_ht: m_htRemise,
        taux_tva: values.tva,
        m_ttc: m_ttc,
        m_tva: m_tva,
        Tva: { label: `${values.tva}%`, value: values.tva },
      };

      const existingRow = addedRows.find(
        (row) => row.designation_article === newRow.designation_article
      );
      if (existingRow) {
        notification.warning({
          message: "Duplication ",
          description: "Cet article a déjà été ajouté à la table.",
        });
        return;
      }

      const updatedRows = [...addedRows, newRow];
      const { totalHT, totalRemise, totalTVA, totalTTC } =
        calculateTotals(updatedRows);
      dispatch({
        type: "SET_STATE",
        payload: {
          addedRows: updatedRows,
          totalHT,
          totalRemise,
          totalTVA,
          totalTTC,
          selectedArticlePrice: null,
        },
      });
      form.resetFields(["code_art", "qte", "remise", "tva"]);
    } else {
      console.error("Certaines valeurs du formulaire ne sont pas définies.");
    }
  };

  const handleDelete = (record) => {
    const updatedRows = addedRows.filter(
      (row) => row.code_art !== record.code_art
    );
    const { totalHT, totalRemise, totalTVA, totalTTC } =
      calculateTotals(updatedRows);
    dispatch({
      type: "SET_STATE",
      payload: {
        addedRows: updatedRows,
        totalHT,
        totalRemise,
        totalTVA,
        totalTTC,
      },
    });
  };

  const exportToExcel = () => {
    const dataToExport = filteredData.map((item) => ({
      "N° Bon": item.numba,
      Date: item.date_ba,
      Fournisseur: item.fournisseur,
      "N° Offre": item.num_offre,
      Statut: item.statut,
      Montant: item.montant_ttc,
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Factures");

    XLSX.writeFile(wb, "factures.xlsx");
  };

  const handleArticleSelect = (value) => {
    const selectedArticle = articlesData.find(
      (article) => article.code_art === value
    );
    if (selectedArticle) {
      dispatch({
        type: "SET_STATE",
        payload: {
          selectedArticlePrice: selectedArticle.prix_unitaire,
          selectedArticle,
        },
      });
    }
  };

  const handleOpenModal = () => {
    dispatch({ type: "SET_STATE", payload: { isModalVisible: true } });
  };

  const handleOpenEditModal = async (item) => {
    dispatch({
      type: "SET_STATE",
      payload: {
        isEditing: true,
        editingItem: item,
        loading: true,
      },
    });
    try {
      const bonData = await bpiService.EditBonAchat(item.numba);
      const bonDataWithDescription = bonData.map((bon) => {
        const article = articlesData.find(
          (article) => article.code_art === bon.code_art
        );
        return {
          ...bon,
          designation_article: article
            ? article.designation_article
            : "Description non trouvée",
        };
      });

      form.setFieldsValue({
        numfourn: item.numfourn,
        date_liv: dayjs(moment(item.date_liv)),
        date_ba: dayjs(moment(item.date_ba)),
        contact: item.contact,
        num_offre: item.num_offre,
        statut: item.statut,
        code_art: "",
        qte: "",
        tva: 18,
        remise: 0,
        qte: 1,
        remarque: item.remarque,
        adresse_liv: item.adresse_liv,
      });

      const { totalHT, totalRemise, totalTVA, totalTTC } = calculateTotals(
        bonDataWithDescription
      );
      dispatch({
        type: "SET_STATE",
        payload: {
          addedRows: bonDataWithDescription,
          totalHT,
          totalRemise,
          totalTVA,
          totalTTC,
          loading: false,
        },
      });
    } catch (error) {
      console.error("Erreur lors de la récupération du Bon d'Achat :", error);
      dispatch({ type: "SET_STATE", payload: { loading: false } });
    }

    dispatch({ type: "SET_STATE", payload: { isModalVisible: true } });
  };

  const handleCloseModal = () => {
    form.resetFields();
    dispatch({
      type: "SET_STATE",
      payload: {
        isModalVisible: false,
        isEditing: false,
        editingItem: null,
        editingRowKey: null,

      },

      //  addedRows: [],
    });
    dispatch({ type: "SET_STATE", payload: { addedRows: [] } });
    dispatch({
      type: "SET_STATE",
      payload: {

        totalHT: 0,
        totalRemise: 0,
        totalTVA: 0,
        totalTTC: 0,

      },
    });
  };

  const handleEditRow = (record) => {
    dispatch({
      type: "SET_STATE",
      payload: { editingRowKey: record.code_art },
    });
  };

  const handleSaveEdit = (record) => {
    dispatch({ type: "SET_STATE", payload: { editingRowKey: null } });
  };

  const handleKeyPress = (event, record) => {
    if (event.key === "Enter") {
      handleSaveEdit(record);
    }
  };

  const handleQteChange = (value, record) => {
    const updatedRows = addedRows.map((row) => {
      if (row.code_art === record.code_art) {
        const updatedRow = { ...row, qte: value };
        return recalculateRow(updatedRow);
      }
      return row;
    });
    updateTotals(updatedRows);
  };

  const handlePrixUnitaireChange = (value, record) => {
    const updatedRows = addedRows.map((row) => {
      if (row.code_art === record.code_art) {
        const updatedRow = { ...row, prix_unitaire: value };
        return recalculateRow(updatedRow);
      }
      return row;
    });
    updateTotals(updatedRows);
  };

  const recalculateRow = (row) => {
    const m_ht = row.qte * row.prix_unitaire;
    const m_htRemise = m_ht - (m_ht * row.m_remise) / 100;
    const m_ttc = m_htRemise + (m_htRemise * row.taux_tva) / 100;
    const m_tva = (m_ht * row.taux_tva) / 100;
    return {
      ...row,
      m_ht: m_htRemise,
      m_ttc: m_ttc,
      m_tva: m_tva,
    };
  };

  const updateTotals = (rows) => {
    const { totalHT, totalRemise, totalTVA, totalTTC } = calculateTotals(rows);
    dispatch({
      type: "SET_STATE",
      payload: {
        addedRows: rows,
        totalHT,
        totalRemise,
        totalTVA,
        totalTTC,
      },
    });
  };

  const calculateTotals = (rows) => {
    let totalHT = 0;
    let totalRemise = 0;
    let totalTVA = 0;
    let totalTTC = 0;

    rows.forEach((row) => {
      totalHT += row.m_ht;
      totalRemise += row.m_remise;
      totalTVA += row.m_tva;
      totalTTC += row.m_ttc;
    });

    return { totalHT, totalRemise, totalTVA, totalTTC };
  };

  const handleSave = async () => {
    if (isEditing) {
      await handleUpdateBonAchat();
    } else {
      await handleAddBonAchat();
    }
  };
  const userId = localStorage.getItem('login');
  const handleAddBonAchat = async () => {
    form
      .validateFields()
      .then(async (values) => {
        const selectedFournisseur = fournis.find(
          (fourni) => fourni.compte === values.numfourn
        );
        const listArticles = addedRows.map((row) => ({
          code_art: row.code_art,
          taux_remise: row.remise,
          Tva: { label: `${row.taux_tva}%`, value: row.taux_tva },
          qte: row.qte,
          prix_unitaire: row.prix_unitaire,
          taux_tva: row.taux_tva,
          m_ht: row.m_ht,
          m_remise: row.m_remise,
          m_ttc: row.m_ttc,
          m_tva: row.m_tva,
          Article: articlesData.find(
            (article) => article.code_art === row.code_art
          ),
        }));

        const docData = {
          Fournisseur: selectedFournisseur,
          adresse_liv: values.adresse_liv || "",
          contact: values.contact || "",
          date_ba: values.date_ba.toISOString(),
          date_liv: values.date_liv.toISOString(),
          listArticles,
          num_offre: values.num_offre || "",
          numfourn: values.numfourn,
          perim: "IPM",
          remarque: values.remarque || "",
          statut: values.statut || "En Cours",
          user_id: userId,
        };

        try {
          await bpiService.AddBonAchat(docData);
          notification.success({
            message: "Ajout réussi",
            description: "Le bon d'achat a été ajouté avec succès.",
          });
          form.resetFields();
          handleCloseModal();
          dispatch({ type: "RESET_FIELDS" });
          dispatch({
            type: "SET_STATE",
            payload: { refreshTable: !refreshTable },
          });
        } catch (error) {
          console.error("Erreur lors de l'ajout du document :", error);
        }
      })
      .catch((info) => {
        console.log("Validation failed:", info);
      });
  };

  const handleUpdateBonAchat = async () => {
    form
      .validateFields()
      .then(async (values) => {
        const selectedFournisseur = fournis.find(
          (fourni) => fourni.compte === values.numfourn
        );
        const listArticles = addedRows.map((row) => ({
          code_art: row.code_art,
          taux_remise: row.remise,
          numbc: editingItem.numba,
          Tva: { label: `${row.taux_tva}%`, value: row.taux_tva },
          qte: row.qte,
          prix_unitaire: row.prix_unitaire,
          taux_tva: row.taux_tva,
          m_ht: row.m_ht,
          m_remise: row.m_remise,
          m_ttc: row.m_ttc,
          m_tva: row.m_tva,
          Article: articlesData.find(
            (article) => article.code_art === row.code_art
          ),
        }));

        const docData = {
          numba: editingItem.numba,
          Fournisseur: selectedFournisseur,
          adresse_liv: values.adresse_liv || "",
          contact: values.contact || "",
          date_ba: values.date_ba.toISOString(),
          date_liv: values.date_liv.toISOString(),
          listArticles,
          num_offre: values.num_offre || "",
          numfourn: values.numfourn,
          perim: "IPM",
          remarque: values.remarque || "",
          statut: values.statut || "En Cours",
          user_id: userId,
        };

        try {
          await bpiService.UpdateBonAchat(docData);
          notification.success({
            message: "Mise à jour réussie",
            description: "Le bon d'achat a été mis à jour avec succès.",
          });
          form.resetFields();
          handleCloseModal();
          dispatch({ type: "RESET_FIELDS" });
          dispatch({
            type: "SET_STATE",
            payload: { refreshTable: !refreshTable },
          });
        } catch (error) {
          console.error("Erreur lors de la mise à jour du document :", error);
        }
      })
      .catch((info) => {
        console.log("Validation failed:", info);
      });
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "decimal",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const yearsOptions = [2023, 2024];

  const monthsOptions = [
    { value: "13", label: "Tous les mois" },
    { value: 1, label: "Janvier" },
    { value: 2, label: "Février" },
    { value: 3, label: "Mars" },
    { value: 4, label: "Avril" },
    { value: 5, label: "Mai" },
    { value: 6, label: "Juin" },
    { value: 7, label: "Juillet" },
    { value: 8, label: "Août" },
    { value: 9, label: "Septembre" },
    { value: 10, label: "Octobre" },
    { value: 11, label: "Novembre" },
    { value: 12, label: "Décembre" },
  ];

  const columns = [
    {
      title: "N° bon",
      dataIndex: "numba",
      key: "numba",
      align: "center",
      render: (text) => <strong>{text || "N/A"}</strong>, // Gérer les valeurs null ou undefined
      sorter: (a, b) => (a.numba || "").localeCompare(b.numba || ""),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Date",
      dataIndex: "date_ba",
      key: "date_ba",
      align: "center",
      render: (text) => (text ? moment(text).format("DD/MM/YYYY") : "N/A"),
      sorter: (a, b) => {
        const dateA = a.date_ba ? new Date(a.date_ba) : 0;
        const dateB = b.date_ba ? new Date(b.date_ba) : 0;
        return dateA - dateB;
      },
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Fournisseur",
      dataIndex: "fournisseur",
      key: "fournisseur",
      sorter: (a, b) => (a.fournisseur || "").localeCompare(b.fournisseur || ""),
      sortDirections: ["ascend", "descend"],
      render: (text) => <span>{text || "N/A"}</span>,
    },
    {
      title: "N° Offre",
      dataIndex: "num_offre",
      key: "num_offre",
      align: "center",
      sorter: (a, b) => (a.num_offre || "").localeCompare(b.num_offre || ""),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Statut",
      dataIndex: "statut",
      key: "statut",
      align: "center",
      sorter: (a, b) => (a.statut || "").localeCompare(b.statut || ""),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            paddingRight: 5,
          }}
        >
          Montant
        </div>
      ),
      dataIndex: "montant_ttc",
      key: "montant_ttc",
      align: "right",
      render: (amount) => (
        <div style={{ paddingRight: 10 }}><strong>{formatAmount(amount || 0)}</strong></div>
      ),
      sorter: (a, b) => (a.montant_ttc || 0) - (b.montant_ttc || 0),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: (
        <div
          style={{
            backgroundColor: "#010f1c",
            color: "white",
            padding: "1px",
          }}
        ></div>
      ),
      dataIndex: "actions",
      key: "actions",
      align: "center",
      fixed: "right",
      render: (text, record) => (
        <span>
          <Tooltip title="Imprimer">
            <BonPdf numba={record.numba} />
          </Tooltip>
          <Tooltip title="Modifier">
            <EditOutlined
              onClick={() => handleOpenEditModal(record)}
              style={{ cursor: "pointer", color: "orange", marginRight: 8 }}
            />
          </Tooltip>
          <Popconfirm
            title="Êtes-vous sûr de vouloir supprimer cette prestation ?"
            onConfirm={() => handleDeleteLine(record.numba)}
            okText="Oui"
            cancelText="Non"
          >
            <Tooltip title="Supprimer">
              <DeleteFilled style={{ cursor: "pointer", color: "red" }} />
            </Tooltip>
          </Popconfirm>
        </span>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle">
        <Col>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 20px",
              background: "linear-gradient(135deg, #1890ff 0%, #0097e6 100%)",
              borderRadius: 10,
              boxShadow: "0 4px 15px rgba(24, 144, 255, 0.3)",
            }}
          >
            <ShoppingCartOutlined style={{ fontSize: 22, color: "#fff" }} />
            <span
              style={{
                fontSize: 18,
                fontWeight: 600,
                color: "#fff",
                letterSpacing: 0.5,
                margin: 0,
              }}
            >
              Bons d'Achat
            </span>
          </div>
        </Col>
        <Col>
          <Select
            bordered={false}
            placeholder="Sélectionnez une année"
            style={{
              width: 120,
              marginRight: 10,
              borderBottom: "2px solid #030303",
            }}
            onChange={(value) =>
              dispatch({ type: "SET_STATE", payload: { selectedYear: value } })
            }
            value={selectedYear}
          >
            {yearsOptions.map((year) => (
              <Option key={year} value={year}>
                {year}
              </Option>
            ))}
          </Select>
          <Select
            bordered={false}
            placeholder="Sélectionnez un mois"
            style={{
              width: 120,
              marginRight: 10,
              borderBottom: "2px solid #030303",
            }}
            onChange={(value) =>
              dispatch({ type: "SET_STATE", payload: { selectedMonth: value } })
            }
            value={selectedMonth}
          >
            {monthsOptions.map((month) => (
              <Option key={month.value} value={month.value}>
                {month.label}
              </Option>
            ))}
          </Select>
          <Input
            placeholder="Recherche..."
            bordered={false}
            style={{
              width: 250,
              marginRight: 10,
              borderBottom: "2px solid #030303",
            }}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <Button
            className="buttonRecep"
            icon={<PlusOutlined />}
            style={{ marginRight: 10 }}
            onClick={handleOpenModal}
          >
            Nouveau
          </Button>
          <Button
            style={{ marginRight: 10 }}
            className="buttonExport"
            onClick={exportToExcel}
          >
            Export Excel
          </Button>
          <Button
            className="buttonRefresh"
            icon={<SyncOutlined />}
            onClick={handleRefresh}
          ></Button>
        </Col>
      </Row>
      <Table
        bordered
        className="custom-header1-table"
        showSorterTooltip={false}
        dataSource={filteredData}
        loading={loading}
        columns={columns}
        pagination={{
          pageSizeOptions: ["5", "10", "20", "50", "100"], // Options disponibles pour la pagination
          showSizeChanger: true, // Permet à l'utilisateur de changer le nombre de lignes par page
          pageSize: pageSize, // Valeur actuelle du nombre de lignes par page
          onShowSizeChange: (current, size) => setPageSize(size), // Mise à jour de pageSize lors du changement
        }}
      />
      <Modal
        title={
          <div>
            {state.isEdit
              ? "Modification Bon Achat"
              : "Création Bon Achat"}

          </div>}
        open={isModalVisible}
        onCancel={handleCloseModal}
        maskClosable={false}
        footer={null}
        style={{ top: 20 }}
        width={1000}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSave}
          initialValues={{
            qte: 1,
            remise: 0,
            tva: 18,
            statut: "En cours",
            remarque: "",
            adresse_liv: "",
          }}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Fournisseur"
                name="numfourn"
                rules={[
                  { required: true, message: "Champ requis", noStyle: true },
                ]}
              >
                <Select
                  style={{ width: "100%" }}
                  defaultValue={"--Selectionner--"}
                  onChange={handleFournisseurSelect}
                  showSearch
                >
                  {fournis.map((fournidata) => (
                    <Option key={fournidata.compte} value={fournidata.compte}>
                      {`${fournidata.compte} - ${fournidata.intitule}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Date Livraison souhaité"
                name="date_liv"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabledDate={(current) => current && current > dayjs().endOf('day')} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Date Enregistrement"
                initialValue={dayjs(moment())}
                name="date_ba"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" disabledDate={(current) => current && current > dayjs().endOf('day')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item label="Contact" name="contact">
                <Input disabled={isContactInputDisabled} />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="N° offre" name="num_offre">
                <Input />
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item
                label="Statut"
                name="statut"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Input
                  style={{ backgroundColor: "ButtonShadow" }}
                  placeholder="en cours"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row
            gutter={16}
            style={{ backgroundColor: "ButtonShadow", marginBottom: "18px" }}
          >
            <Col span={8}>
              <Form.Item label="Article" name="code_art">
                <Select
                  style={{ width: "100%" }}
                  defaultValue={"--Selectionner--"}
                  onChange={handleArticleSelect}
                  value={selectedArticle}
                  showSearch
                >
                  {articlesData.map((article) => (
                    <Option key={article.code_art} value={article.code_art}>
                      {`${article.designation_article}`}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                label="Quantité"
                name="qte"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <InputNumber defaultValue={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="P.Unitaire (0)"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Input value={selectedArticlePrice} />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item
                label="TVA (%)"
                name="tva"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <Select style={{ width: "100%" }} defaultValue={18}>
                  <Option value={0}></Option>
                  <Option value={18}>18%</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item
                label="Remise (%)"
                name="remise"
                rules={[{ required: true, message: "Champ requis" }]}
              >
                <InputNumber defaultValue={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <div style={{ marginTop: "30px" }}>
              <Button
                onClick={handleAddRow}
                style={{ backgroundColor: "green" }}
              >
                Valider
              </Button>
            </div>
          </Row>
          <Table
            style={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              width: "100%",
              marginBottom: "18px",
            }}
            className="custom-header1-table"
            bordered
            dataSource={[...addedRows]}
            scroll={{ x: true }}
            pagination={{
              pageSizeOptions: ["5", "10", "20", "50", "100"], // Options disponibles pour la pagination
              showSizeChanger: true, // Permet à l'utilisateur de changer le nombre de lignes par page
              pageSize: pageSize, // Valeur actuelle du nombre de lignes par page
              onShowSizeChange: (current, size) => setPageSize(size), // Mise à jour de pageSize lors du changement
            }}
            columns={[
              {
                title: "Code Art.",
                dataIndex: "code_art",
                key: "code_art",
                align: "center",
              },
              {
                title: "Description",
                dataIndex: "designation_article",
                key: "designation_article",
              },
              {
                title: "Qte",
                dataIndex: "qte",
                key: "qte",
                align: "center",
                render: (text, record) =>
                  editingRowKey === record.code_art ? (
                    <InputNumber
                      defaultValue={text}
                      onChange={(value) => handleQteChange(value, record)}
                      onKeyPress={(event) => handleKeyPress(event, record)}
                    />
                  ) : (
                    text
                  ),
              },
              {
                title: "P. unitaire",
                dataIndex: "prix_unitaire",
                key: "prix_unitaire",
                align: "right",
                render: (text, record) =>
                  editingRowKey === record.code_art ? (
                    <InputNumber
                      defaultValue={text}
                      onChange={(value) =>
                        handlePrixUnitaireChange(value, record)
                      }
                      onKeyPress={(event) => handleKeyPress(event, record)}
                    />
                  ) : (
                    text
                  ),
              },
              {
                title: "Remise",
                dataIndex: "m_remise",
                key: "m_remise",
                align: "right",
              },
              {
                title: "Montant HT",
                dataIndex: "m_ht",
                key: "m_ht",
                align: "right",
              },
              {
                title: "TVA",
                dataIndex: "taux_tva",
                key: "taux_tva",
                align: "center",
              },
              {
                title: "Montant TTC",
                dataIndex: "m_ttc",
                key: "m_ttc",
                align: "right",
              },
              {
                title: (
                  <div
                    style={{
                      backgroundColor: "#010f1c",
                      color: "white",
                      padding: "1px",
                    }}
                  ></div>
                ),
                dataIndex: "actions",
                key: "actions",
                align: "center",
                fixed: "right",
                render: (text, record) => (
                  <span>
                    <Tooltip title="Modifier">
                      <EditOutlined
                        onClick={() => handleEditRow(record)}
                        style={{
                          cursor: "pointer",
                          color: "blue",
                          marginRight: 8,
                        }}
                      />
                    </Tooltip>
                    <Popconfirm
                      title="Êtes-vous sûr de vouloir supprimer cette prestation ?"
                      onConfirm={() => handleDelete(record)}
                      okText="Oui"
                      cancelText="Non"
                    >
                      <Tooltip title="Supprimer">
                        <DeleteFilled
                          style={{ cursor: "pointer", color: "red" }}
                        />
                      </Tooltip>
                    </Popconfirm>
                  </span>
                ),
              },
            ]}
          />
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="remarque">
                <TextArea
                  showCount
                  maxLength={50}
                  placeholder="Remarque (optionnel)..."
                />
              </Form.Item>
              <Form.Item name="adresse_liv">
                <Input
                  placeholder="Adresse de livraison"
                  style={{ marginTop: "20px" }}
                />
              </Form.Item>
              <h3 style={{ color: "#03284a" }}>
                {`Contact : ${infosGenData ? infosGenData.contact : ""} - ${infosGenData ? infosGenData.email_contact : ""
                  }`}
              </h3>
            </Col>
            <Col span={12}>
              <List size="small" bordered>
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>Total HT:</strong>
                  <strong style={{ color: "green" }}>{totalHT}</strong>
                </List.Item>
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>Total Remise:</strong>
                  <strong style={{ color: "green" }}>{totalRemise}</strong>
                </List.Item>
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>Total TVA:</strong>
                  <strong style={{ color: "green" }}>{totalTVA}</strong>
                </List.Item>
                <List.Item
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <strong>Total TTC:</strong>
                  <strong style={{ color: "green" }}>{totalTTC}</strong>
                </List.Item>
              </List>
            </Col>
          </Row>
          <div style={{ marginTop: "20px", textAlign: "right" }}>
            <Form.Item>
              <Button onClick={handleCloseModal} type="dashed" danger>
                Annuler
              </Button>
              <Button
                htmlType="submit"
                type="primary"
                style={{ backgroundColor: "#010f1c", marginLeft: 8 }}
              >
                {isEditing ? "Modifier" : "Enregistrer"}
              </Button>
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default BonsAchat;
